import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const sliderVariants = cva(
  'w-full appearance-none cursor-pointer rounded-lg bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      size: {
        sm: 'h-1',
        md: 'h-2',
        lg: 'h-3',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

export interface SliderProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'>,
    VariantProps<typeof sliderVariants> {
  min?: number;
  max?: number;
  step?: number;
}

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  ({ size, className, min = 0, max = 100, step = 1, ...props }, ref) => {
    return (
      <>
        <input
          ref={ref}
          type="range"
          min={min}
          max={max}
          step={step}
          className={cn(sliderVariants({ size }), 'slider-input', className)}
          {...props}
        />
        <style>{`
          /* Webkit (Chrome, Safari, Edge) */
          .slider-input::-webkit-slider-thumb {
            appearance: none;
            width: ${size === 'sm' ? '12px' : size === 'lg' ? '20px' : '16px'};
            height: ${size === 'sm' ? '12px' : size === 'lg' ? '20px' : '16px'};
            border-radius: 50%;
            background: rgb(var(--color-primary));
            cursor: pointer;
            border: 2px solid rgb(var(--color-background));
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
          }

          .slider-input::-webkit-slider-thumb:hover {
            background: rgb(var(--color-primary) / 0.9);
          }

          .slider-input:focus-visible::-webkit-slider-thumb {
            outline: 2px solid rgb(var(--color-ring));
            outline-offset: 2px;
          }

          /* Firefox */
          .slider-input::-moz-range-thumb {
            width: ${size === 'sm' ? '12px' : size === 'lg' ? '20px' : '16px'};
            height: ${size === 'sm' ? '12px' : size === 'lg' ? '20px' : '16px'};
            border-radius: 50%;
            background: rgb(var(--color-primary));
            cursor: pointer;
            border: 2px solid rgb(var(--color-background));
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
          }

          .slider-input::-moz-range-thumb:hover {
            background: rgb(var(--color-primary) / 0.9);
          }

          .slider-input:focus-visible::-moz-range-thumb {
            outline: 2px solid rgb(var(--color-ring));
            outline-offset: 2px;
          }

          .slider-input::-moz-range-track {
            background: rgb(var(--color-muted) / 0.2);
            border-radius: 8px;
          }
        `}</style>
      </>
    );
  }
);

Slider.displayName = 'Slider';
